import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { throttle, debounce, } from 'lodash';
import { Teasable } from '@zattoo/zapi/lib/editorial/enums';
import { getLanguageNames, updateBroadcastPosition, updateVODStatus, } from '@zattoo/zapi';
import { ChannelLogger, ConsoleLogger, ErrorCode, EventKey, AssetType, PlaybackState, isPinError, isZapiPlaybackError, AdEventKey, } from '@zattoo/playback-sdk-zapi';
import { AdType, SeekResultType, } from '@zattoo/playback-sdk';
import { TimeshiftStatus } from '@zattoo/zapi/lib/enums';
import { generateAdId, isGt12AdType, sdkAdTypeToGT12TrackingAdType, } from '../gt12-tracking/utils/index';
import { styles } from './styles';
import { createPlayer } from '../player';
import { PlayerView } from '../player/view';
import { UTVPlayerEvents, UTVPlayerStreamType, UTVPlayerErrorType, QualityRestrictedType, } from './interfaces';
import { initialProgramInfo, playerInitialState, } from './states';
import { getISOLanguageName, imaCuePointsToUTVCueRanges, setMediaTrackById, updatePlayOptions, } from './utils';
import * as gt12Tracking from '../gt12-tracking/index';
import { GT12AdType, GT12EventType, GT12StreamType, } from '../gt12-tracking/enums';
import { SkipAdButton } from './ads';
const mapAssetTypeToStreamType = (assetType, requestedStreamType) => {
    switch (assetType) {
        case AssetType.LIVE:
            return UTVPlayerStreamType.LIVE;
        case AssetType.REPLAY:
            return UTVPlayerStreamType.REPLAY;
        case AssetType.RECORDING:
            return UTVPlayerStreamType.RECORDING;
        case AssetType.VOD:
            return requestedStreamType;
        case AssetType.VOD_TRAILER:
            return UTVPlayerStreamType.VOD_TRAILER;
        case AssetType.REGISTERED_TIMESHIFT:
            return UTVPlayerStreamType.TIMESHIFT;
        default:
            return requestedStreamType;
    }
};
const eventToPlayerEvent = (event) => {
    switch (event.type) {
        case EventKey.MEDIA_CHANGED:
        case EventKey.CURRENT_POSITION_CHANGED:
        case EventKey.PLAYER_STATE_CHANGED:
        case EventKey.PLAYBACK_STATE_CHANGED:
        case EventKey.AVAILABLE_AUDIO_TRACKS_CHANGED:
        case EventKey.AVAILABLE_SUBTITLES_TRACKS_CHANGED:
            return UTVPlayerEvents.STATE_UPDATED;
        case EventKey.SELECTED_AUDIO_TRACK_CHANGED:
        case EventKey.SELECTED_SUBTITLES_TRACK_CHANGED:
            return UTVPlayerEvents.TRACK_UPDATED;
        default:
            return UTVPlayerEvents.UNKNOWN;
    }
};
const OFF_SUBTITLE_TRACK = {
    id: 'off',
    locale: 'off',
    label: 'Off',
};
const PLAY_DEBOUNCE_MS = 500;
const PLAY_NEXT_PROGRAM_MS = 100;
const PREPARE_NEXT_PROGRAM_MS = 60_000;
const STATE_UPDATED_EVENT_THROTTLE_MS = 400;
const ZAPI_LAST_POSITION_UPDATE_THROTTLE_MS = 1000;
const POSITION_SYNC_INTERVAL_MS = 60_000;
const POSITION_SYNC_DEBOUNCE_MS = 500;
let globalInit = () => Promise.resolve();
// This is called on app side depending on platform, to override globalInit
export const initPlaybackSdk = (init) => {
    globalInit = init;
};
export const UTVPlayerView = forwardRef(({ onEvent, host, publicId, appVersion, appId, stepForwardDuration, stepBackwardDuration, license, locale, debug, style = styles.player, isAnalyticsProd = false, enableEventDispatcher, enableMediaSession, isCastEnabled, }, ref) => {
    const [playerCreated, setPlayerCreated] = useState(false);
    const playerInstanceRef = useRef(null);
    const playerStateRef = useRef(playerInitialState);
    const broadcastId = useRef(undefined);
    const vodTeasableId = useRef(undefined);
    const vodTeasableType = useRef(undefined);
    const programInfoRef = useRef(initialProgramInfo);
    const previousProgramInfoRef = useRef(initialProgramInfo);
    const languageNamesRef = useRef(undefined);
    const psid = useRef(undefined);
    // Used for when unmuting, to go back to previous value. Default as max volume
    const previousVolumeRef = useRef(1);
    const ignoreEventsWhileLoading = useRef(false);
    const ignoreEventsAfterMediaEnded = useRef(false);
    const ignoreTimingUpdates = useRef(true);
    const pendingPlayCall = useRef(undefined);
    const resolveNewAssetTypeForPlayRequested = useRef(undefined);
    const playOptionQueue = useRef([]);
    const requestedOptions = useRef(undefined);
    const emittedCloseToEnd = useRef(false);
    const previousStream = useRef(undefined);
    // Reference to the ad cue points from the AD_METADATA event
    // This does not include the prems of the stream
    const adCuePointsRef = useRef([]);
    // Reference to the ad break or pause Ad ID
    // to be used as ad_id in tracking GT12 Ad events
    const adBreakIdRef = useRef(null);
    /**
     * Reference to the ad ID for the PauseAd displayed events.
     * So that we can track the pause Ad events only once
     */
    const pauseAdDisplayedSentForAdIdRef = useRef(null);
    const pauseAdTenSecondsDisplayedSentForAdIdRef = useRef(null);
    // reference for the interval for sending position updates to consumer apps
    const positionSyncIntervalRef = useRef(null);
    // Reference to the current Ad
    // taken from AD_LOADED event, used on AD_STARTED and AD_ENDED events
    const currentAdRef = useRef(null);
    /** Until the SDK can give us a way to differentiate between
     * Registered Timeshift and Unregistered Timeshift,
     * we use this ref listening to the watch response.
     * If not, we will keep this.
     *
     * @todo https://zattoo2.atlassian.net/browse/WEX-313
     */
    const unregisteredTimeshiftAvailability = useRef(undefined);
    const debugLogger = useCallback((error) => {
        if (debug) {
            console.error(error);
        }
    }, [debug]);
    const throttleStateUpdatedEvent = useMemo(() => onEvent && throttle(onEvent, STATE_UPDATED_EVENT_THROTTLE_MS), [onEvent]);
    const throttleCloseToEndEvent = useMemo(
    // There are some edge cases when system time is incorrect
    // we throttle CLOSE_TO_END event to avoid multiple events
    // @todo: Check if this is really needed
    () => onEvent && throttle(onEvent, 10_000), [onEvent]);
    const updateUTVPlayerState = useCallback((type, newState, data, error) => {
        if (type === UTVPlayerEvents.PLAY_REQUESTED || type === UTVPlayerEvents.STREAM_READY) {
            // cancel previously queued STATE_UDATED event
            throttleStateUpdatedEvent?.cancel();
            previousStream.current = playerStateRef.current.stream;
        }
        const nextState = {
            ...playerStateRef.current,
            ...newState,
        };
        playerStateRef.current = nextState;
        if (!onEvent) {
            return;
        }
        const newEvent = {
            event: type,
            state: nextState,
            data: data || undefined,
            error: error || undefined,
        };
        if (type === UTVPlayerEvents.STATE_UPDATED) {
            throttleStateUpdatedEvent?.(newEvent);
        }
        else if (type === UTVPlayerEvents.CLOSE_TO_END) {
            throttleCloseToEndEvent?.(newEvent);
        }
        else {
            onEvent(newEvent);
        }
    }, [onEvent, throttleStateUpdatedEvent, throttleCloseToEndEvent]);
    const triggerError = useCallback((type, state = {}, data) => {
        const nextState = {
            ...playerStateRef.current,
            ...state,
        };
        playerStateRef.current = nextState;
        updateUTVPlayerState(UTVPlayerEvents.ERROR, nextState, {}, {
            errorType: type,
            errorData: data,
        });
    }, [updateUTVPlayerState]);
    // position is either ISOstring for broadcast or number in seconds for VOD
    const sendPositionSyncEvent = useCallback((data) => {
        if (data) {
            updateUTVPlayerState(UTVPlayerEvents.POSITION_SYNC, {}, data);
            return;
        }
        const stream = playerStateRef.current?.stream;
        if (!stream ||
            stream?.streamType === UTVPlayerStreamType.LIVE ||
            stream?.streamType === UTVPlayerStreamType.VOD_TRAILER ||
            playerStateRef.current?.currentMs === undefined) {
            return;
        }
        if ((stream.streamType === UTVPlayerStreamType.VOD_MOVIE ||
            stream.streamType === UTVPlayerStreamType.VOD_EPISODE) && vodTeasableId.current && vodTeasableType.current) {
            updateUTVPlayerState(UTVPlayerEvents.POSITION_SYNC, {}, {
                position: Math.floor(playerStateRef.current.currentMs / 1000),
                id: vodTeasableId.current,
                teasableType: vodTeasableType.current,
                isVod: true,
            });
            return;
        }
        if (playerStateRef.current.currentMs !== undefined &&
            broadcastId.current &&
            programInfoRef.current.programStartEpochMs !== undefined) {
            const positionMs = (programInfoRef.current.programStartEpochMs + playerStateRef.current.currentMs);
            const positionISO = new Date(positionMs).toISOString();
            updateUTVPlayerState(UTVPlayerEvents.POSITION_SYNC, {}, {
                position: positionISO,
                id: broadcastId.current,
                lastStreamedAt: new Date().toISOString(),
                isVod: false,
            });
        }
    }, [updateUTVPlayerState]);
    const debouncedSendPositionSyncEvent = useMemo(() => debounce(sendPositionSyncEvent, POSITION_SYNC_DEBOUNCE_MS), [sendPositionSyncEvent]);
    const clearPositionSyncInterval = useCallback(() => {
        if (positionSyncIntervalRef.current) {
            clearInterval(positionSyncIntervalRef.current);
            positionSyncIntervalRef.current = null;
        }
    }, []);
    const startPositionSyncInterval = useCallback(() => {
        // Clear any existing interval first to ensure only one exists
        clearPositionSyncInterval();
        positionSyncIntervalRef.current = setInterval(() => {
            sendPositionSyncEvent();
        }, POSITION_SYNC_INTERVAL_MS);
    }, [clearPositionSyncInterval, sendPositionSyncEvent]);
    const updateLastPlayedPosition = useCallback((programStartEpochMs, stream) => {
        if (!stream || // gate for when its the first play call
            stream.streamType === UTVPlayerStreamType.LIVE || // dont update position for Live
            stream.streamType === UTVPlayerStreamType.VOD_TRAILER // dont update position for VOD Trailer
        ) {
            return;
        }
        if (stream.streamType === UTVPlayerStreamType.VOD_MOVIE ||
            stream.streamType === UTVPlayerStreamType.VOD_EPISODE) {
            if (playerStateRef.current.currentMs !== undefined
                && vodTeasableId.current
                && vodTeasableType.current) {
                updateVODStatus({
                    teasable_id: vodTeasableId.current,
                    teasable_type: vodTeasableType.current,
                    position: Math.floor(playerStateRef.current.currentMs / 1000),
                }).then((response) => {
                    sendPositionSyncEvent({
                        isVod: true,
                        teasableType: response.teasable_type,
                        position: response.position,
                        id: String(response.teasable_id),
                    });
                });
            }
            return;
        }
        if (playerStateRef.current.currentMs !== undefined && broadcastId.current) {
            // Capture broadcastId before async call
            const currentBroadcastId = broadcastId.current;
            updateBroadcastPosition({
                broadcast_id: currentBroadcastId,
                position: (programStartEpochMs + playerStateRef.current.currentMs) / 1000,
            }).then((response) => {
                const broadcastResponseData = response.broadcast_state[currentBroadcastId];
                if (!broadcastResponseData) {
                    return;
                }
                sendPositionSyncEvent({
                    isVod: false,
                    id: currentBroadcastId,
                    position: broadcastResponseData.position,
                    lastStreamedAt: broadcastResponseData.last_streamed_at,
                });
            });
        }
    }, [sendPositionSyncEvent]);
    const saveStreamProgressPosition = useCallback(() => {
        // save the position on stream stop
        updateLastPlayedPosition(programInfoRef.current?.programStartEpochMs, playerStateRef.current?.stream);
    }, [updateLastPlayedPosition]);
    const playCall = useCallback(async (options) => {
        // Temporary solution to avoid raise condition
        await pendingPlayCall.current;
        switch (options?.streamType) {
            case UTVPlayerStreamType.VOD_MOVIE: {
                const { permissionToken, teasableId, streamingOptions, ...playOptions } = options;
                if (streamingOptions) {
                    return playerInstanceRef.current?.playVodRakuten({
                        token: permissionToken,
                        teasableId,
                        teasableType: Teasable.VOD_MOVIE,
                        streamingOptions,
                        playOptions: updatePlayOptions(playOptions, isCastEnabled),
                    }).then(() => {
                        vodTeasableId.current = teasableId;
                        vodTeasableType.current = Teasable.VOD_MOVIE;
                    }).catch(debugLogger);
                }
                return playerInstanceRef.current?.playVod(permissionToken, teasableId, Teasable.VOD_MOVIE, updatePlayOptions(playOptions)).then(() => {
                    vodTeasableId.current = teasableId;
                    vodTeasableType.current = Teasable.VOD_MOVIE;
                }).catch(debugLogger);
            }
            case UTVPlayerStreamType.VOD_EPISODE: {
                const { permissionToken, teasableId, streamingOptions, ...playOptions } = options;
                if (streamingOptions) {
                    return playerInstanceRef.current?.playVodRakuten({
                        token: permissionToken,
                        teasableId,
                        teasableType: Teasable.VOD_SERIES_EPISODE,
                        streamingOptions,
                        playOptions: updatePlayOptions(playOptions, isCastEnabled),
                    }).then(() => {
                        vodTeasableId.current = teasableId;
                        vodTeasableType.current = Teasable.VOD_SERIES_EPISODE;
                    }).catch(debugLogger);
                }
                return playerInstanceRef.current?.playVod(permissionToken, teasableId, Teasable.VOD_SERIES_EPISODE, updatePlayOptions(playOptions, isCastEnabled)).then(() => {
                    vodTeasableId.current = teasableId;
                    vodTeasableType.current = Teasable.VOD_SERIES_EPISODE;
                }).catch(debugLogger);
            }
            case UTVPlayerStreamType.VOD_TRAILER: {
                const { teasableId, trailerAudioLanguages, ...playOptions } = options;
                if (trailerAudioLanguages) {
                    return playerInstanceRef.current?.playVodTrailerRakuten({
                        teasableId,
                        teasableType: Teasable.VOD_MOVIE,
                        trailerAudioLanguages,
                        playOptions: updatePlayOptions(playOptions, isCastEnabled),
                    }).then(() => {
                        vodTeasableId.current = teasableId;
                        vodTeasableType.current = Teasable.VOD_MOVIE;
                    }).catch(debugLogger);
                }
                return playerInstanceRef.current?.playVodTrailer({
                    teasableId,
                    teasableType: Teasable.VOD_MOVIE,
                    playOptions: updatePlayOptions(playOptions, isCastEnabled),
                }).then(() => {
                    vodTeasableId.current = teasableId;
                    vodTeasableType.current = Teasable.VOD_MOVIE;
                }).catch(debugLogger);
            }
            case UTVPlayerStreamType.LIVE: {
                const { channelId, programId, ...playOptions } = options;
                return playerInstanceRef.current?.playLive(channelId, updatePlayOptions(playOptions, isCastEnabled)).then(() => {
                    // Save broadcastId for timeshift mode
                    // so that position tracking and resume functionality can correctly associate playback position
                    // with the right broadcast.
                    broadcastId.current = programId;
                }).catch(debugLogger);
            }
            case UTVPlayerStreamType.REPLAY: {
                const { channelId, programId, ...playOptions } = options;
                return playerInstanceRef.current?.playProgram(channelId, programId, updatePlayOptions(playOptions, isCastEnabled)).then(() => {
                    broadcastId.current = programId;
                }).catch(debugLogger);
            }
            case UTVPlayerStreamType.RECORDING: {
                const { programId, recordingId, ...playOptions } = options;
                return playerInstanceRef.current?.playRecording(recordingId, updatePlayOptions(playOptions, isCastEnabled)).then(() => {
                    broadcastId.current = programId;
                }).catch(debugLogger);
            }
            default: {
                return playerInstanceRef.current?.play();
            }
        }
    }, [debugLogger, isCastEnabled]);
    const callPlay = useCallback((options) => {
        pendingPlayCall.current = playCall(options)
            .then(() => {
            pendingPlayCall.current = undefined;
        });
    }, [playCall]);
    const setLiveProgramInfoCallback = useCallback(() => {
        if (!broadcastId.current ||
            !programInfoRef.current.programStartEpochMs ||
            !programInfoRef.current.programEndEpochMs) {
            return Promise.reject(new Error('Missing required program information for live stream'));
        }
        const duration = (programInfoRef.current.programEndEpochMs - programInfoRef.current.programStartEpochMs) / 1000;
        return Promise.resolve({
            programId: broadcastId.current,
            start: programInfoRef.current.programStartEpochMs / 1000,
            duration,
        });
    }, []);
    const setProgramInfo = useCallback(({ programEndEpochMs, programId, programStartEpochMs, programDescriptor, }) => {
        const programInfoChanged = broadcastId.current !== programId;
        previousProgramInfoRef.current = { ...programInfoRef.current };
        programInfoRef.current.programStartEpochMs = programStartEpochMs;
        programInfoRef.current.programEndEpochMs = programEndEpochMs;
        broadcastId.current = programId;
        if (programInfoChanged) {
            gt12Tracking.endSession();
        }
        const currentOptions = requestedOptions.current;
        const channelId = currentOptions && (currentOptions.streamType === UTVPlayerStreamType.LIVE ||
            currentOptions.streamType === UTVPlayerStreamType.REPLAY ||
            currentOptions.streamType === UTVPlayerStreamType.RECORDING) ? (currentOptions).channelId : undefined;
        if (programInfoChanged && channelId && programId) {
            gt12Tracking.startSession({
                channel_id: channelId,
                program_id: programId,
            });
        }
        // Clear cue queue to allow fresh cuePlay when stream is close to end of new program
        // The queued program from previous program info is no longer valid
        playOptionQueue.current = [];
        emittedCloseToEnd.current = false;
        if (playerStateRef.current.stream?.streamType === UTVPlayerStreamType.LIVE) {
            playerInstanceRef.current?.setProgramInfoCallback(setLiveProgramInfoCallback);
        }
        const assetType = playerInstanceRef.current?.assetType;
        const playerDuration = playerInstanceRef.current?.duration ?? 0;
        const durationMs = isFinite(playerDuration) ? playerDuration * 1000 : 0;
        const isDynamicLengthStream = assetType === AssetType.LIVE || assetType === AssetType.REGISTERED_TIMESHIFT;
        const programEndMs = assetType === AssetType.VOD
            ? durationMs - programInfoRef.current.preMs
            : programInfoRef.current.programEndEpochMs - programInfoRef.current.programStartEpochMs;
        // Recalculate timings immediately for dynamic streams to update progress bar without waiting for next SDK event
        // For dynamic streams, timings are relative to programStartEpochMs, so we adjust by the delta
        let recalculatedCurrentMs = playerStateRef.current.currentMs;
        let recalculatedStreamStartMs = playerStateRef.current.streamStartMs;
        let recalculatedStreamEndMs = playerStateRef.current.streamEndMs;
        if (isDynamicLengthStream) {
            const programStartDelta = programStartEpochMs - previousProgramInfoRef.current.programStartEpochMs;
            if (recalculatedCurrentMs !== undefined) {
                recalculatedCurrentMs -= programStartDelta;
            }
            if (recalculatedStreamStartMs !== undefined) {
                recalculatedStreamStartMs -= programStartDelta;
            }
            if (recalculatedStreamEndMs !== undefined) {
                recalculatedStreamEndMs -= programStartDelta;
            }
        }
        const newState = {
            programStartMs: 0,
            programEndMs,
            stream: {
                ...playerStateRef.current.stream,
                streamType: playerStateRef.current.stream?.streamType ?? UTVPlayerStreamType.LIVE,
                programDescriptor: {
                    ...(playerStateRef.current.stream?.programDescriptor || {}),
                    ...(programDescriptor || {}),
                },
            },
        };
        if (recalculatedCurrentMs !== undefined) {
            newState.currentMs = recalculatedCurrentMs;
        }
        if (recalculatedStreamStartMs !== undefined) {
            newState.streamStartMs = recalculatedStreamStartMs;
        }
        if (recalculatedStreamEndMs !== undefined) {
            newState.streamEndMs = recalculatedStreamEndMs;
        }
        updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, newState);
        const finalCurrentMs = recalculatedCurrentMs ?? playerStateRef.current.currentMs;
        if (finalCurrentMs !== undefined
            && programEndMs !== undefined
            && finalCurrentMs >= programEndMs - PREPARE_NEXT_PROGRAM_MS
            && finalCurrentMs < programEndMs) {
            updateUTVPlayerState(UTVPlayerEvents.CLOSE_TO_END);
            emittedCloseToEnd.current = true;
        }
    }, [updateUTVPlayerState, setLiveProgramInfoCallback]);
    const setVolume = useCallback((volume) => {
        if (volume < 0 || volume > 1) {
            return;
        }
        previousVolumeRef.current = playerStateRef.current?.volume;
        playerInstanceRef.current?.setVolume(volume);
        updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, {
            volume,
        });
    }, [updateUTVPlayerState]);
    const volumeUp = useCallback(() => {
        const currentVolume = playerStateRef.current?.volume ?? 1;
        // multiplication by 10 is to avoid floating point issues
        const newVolume = Math.min(((currentVolume * 10) + 1) / 10, 1); // Ensure volume doesn't exceed 1
        if (newVolume !== currentVolume) {
            previousVolumeRef.current = currentVolume;
            playerInstanceRef.current?.setVolume(newVolume);
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, { volume: newVolume });
        }
    }, [updateUTVPlayerState]);
    const volumeDown = useCallback(() => {
        const currentVolume = playerStateRef.current?.volume ?? 1;
        // multiplication by 10 is to avoid floating point issues
        const newVolume = Math.max(((currentVolume * 10) - 1) / 10, 0); // Ensure volume doesn't go below 0
        if (newVolume !== currentVolume) {
            previousVolumeRef.current = currentVolume;
            playerInstanceRef.current?.setVolume(newVolume);
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, { volume: newVolume });
        }
    }, [updateUTVPlayerState]);
    const toggleMute = useCallback(() => {
        const currentVolume = playerStateRef.current?.volume ?? 1;
        if (currentVolume > 0) {
            // Save current volume and mute
            previousVolumeRef.current = currentVolume;
            playerInstanceRef.current?.setVolume(0);
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, { volume: 0 });
        }
        else {
            // Restore previous volume
            const restoredVolume = previousVolumeRef.current ?? 1;
            playerInstanceRef.current?.setVolume(restoredVolume);
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, { volume: restoredVolume });
        }
    }, [updateUTVPlayerState]);
    const removePauseAdOnSeek = useCallback((seekResultType) => {
        if (playerStateRef.current?.pauseAd &&
            (seekResultType !== SeekResultType.GT12_FORBIDDEN &&
                seekResultType !== SeekResultType.FORWARD_SEEKING_NOT_ALLOWED)) {
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, { pauseAd: undefined });
        }
    }, [updateUTVPlayerState]);
    const handleSeekResult = useCallback((seekResult) => {
        if (!seekResult) {
            return;
        }
        removePauseAdOnSeek(seekResult.type);
        debouncedSendPositionSyncEvent();
    }, [removePauseAdOnSeek, debouncedSendPositionSyncEvent]);
    // Positive and negative numbers
    const seek = useCallback(async (numSteps) => {
        let seekResult;
        if (numSteps > 0) {
            seekResult = await playerInstanceRef.current?.seekForward(numSteps);
        }
        else if (numSteps < 0) {
            seekResult = await playerInstanceRef.current?.seekBackward(Math.abs(numSteps));
        }
        handleSeekResult(seekResult);
    }, [handleSeekResult]);
    // Seek to position (relative program time ms)
    const seekTo = useCallback((positionMs) => {
        if (positionMs >= 0) {
            const newPositionMs = positionMs - (playerStateRef.current.streamStartMs ?? 0);
            const seekResult = playerInstanceRef.current?.seek(newPositionMs / 1000);
            handleSeekResult(seekResult);
        }
    }, [handleSeekResult]);
    const isSameStream = useCallback((options) => {
        if (requestedOptions.current?.streamType !== options?.streamType
            || playerStateRef.current.stream?.streamType !== options?.streamType
            || options.pin
            || playerStateRef.current.stopped) {
            return false;
        }
        switch (options?.streamType) {
            case UTVPlayerStreamType.VOD_MOVIE: {
                const { permissionToken, teasableId, } = requestedOptions.current;
                return (permissionToken === options.permissionToken
                    && teasableId === options.teasableId);
            }
            case UTVPlayerStreamType.VOD_EPISODE: {
                const { permissionToken, teasableId, } = requestedOptions.current;
                return (permissionToken === options.permissionToken
                    && teasableId === options.teasableId);
            }
            case UTVPlayerStreamType.VOD_TRAILER: {
                const { teasableId, } = requestedOptions.current;
                return teasableId === options.teasableId;
            }
            case UTVPlayerStreamType.LIVE: {
                const { channelId, } = requestedOptions.current;
                if (playerStateRef.current.paused) {
                    return false;
                }
                return channelId === options.channelId;
            }
            case UTVPlayerStreamType.REPLAY: {
                const { channelId, programId, } = requestedOptions.current;
                return (channelId === options.channelId
                    && programId === options.programId);
            }
            case UTVPlayerStreamType.RECORDING: {
                const { programId, } = requestedOptions.current;
                return programId === options.programId;
            }
            default: {
                return false;
            }
        }
    }, []);
    const play = useCallback((options, fromQueue, continuousStream) => {
        emittedCloseToEnd.current = false;
        if (!options) {
            if (playerStateRef.current.stream?.streamType === UTVPlayerStreamType.LIVE) {
                if (unregisteredTimeshiftAvailability.current === TimeshiftStatus.AVAILABLE) {
                    const waitForNewAssetType = new Promise((resolve) => {
                        resolveNewAssetTypeForPlayRequested.current = resolve;
                    });
                    // This looks like a hack to find out Registered Timeshift or Unregistered Timeshift
                    waitForNewAssetType.then((assetType) => {
                        resolveNewAssetTypeForPlayRequested.current = undefined;
                        clearPositionSyncInterval();
                        updateUTVPlayerState(UTVPlayerEvents.STREAM_READY, {
                            canPlayLive: assetType ? playerStateRef.current.canPlayLive : true,
                            stream: {
                                ...playerStateRef.current.stream,
                                streamType: assetType ?
                                    mapAssetTypeToStreamType(assetType, UTVPlayerStreamType.LIVE) :
                                    UTVPlayerStreamType.REPLAY,
                            },
                            ad: undefined,
                            isAdPlayback: false,
                            cueRanges: [],
                            skipAdAvailable: false,
                            isChannelAd: false,
                        });
                        adCuePointsRef.current = [];
                    });
                }
            }
            updateUTVPlayerState(UTVPlayerEvents.PLAY_REQUESTED, {
                pauseAd: undefined,
            });
            playerInstanceRef.current?.play();
            return;
        }
        // save the position of the current one before requesting a new stream
        updateLastPlayedPosition(programInfoRef.current?.programStartEpochMs, playerStateRef.current?.stream);
        if ((options.streamType === UTVPlayerStreamType.LIVE ||
            options.streamType === UTVPlayerStreamType.REPLAY ||
            options.streamType === UTVPlayerStreamType.RECORDING) &&
            options.channelId &&
            options.programId) {
            gt12Tracking.startSession({
                channel_id: options.channelId,
                program_id: options.programId,
            });
        }
        else {
            gt12Tracking.endSession();
            currentAdRef.current = null;
            adBreakIdRef.current = null;
        }
        if (options.streamType === UTVPlayerStreamType.LIVE) {
            playerInstanceRef.current?.setProgramInfoCallback(setLiveProgramInfoCallback);
        }
        if (!fromQueue) {
            playOptionQueue.current = [];
        }
        const sameStream = isSameStream(options);
        updateUTVPlayerState(UTVPlayerEvents.PLAY_REQUESTED, {
            stream: {
                streamType: options.streamType,
                programDescriptor: options.programDescriptor,
            },
            ...(!sameStream && {
                cueRanges: [],
                isAdPlayback: false,
                ad: undefined,
                pauseAd: undefined,
                skipAdAvailable: false,
                isChannelAd: false,
            }),
        });
        if (!sameStream) {
            adCuePointsRef.current = [];
        }
        if (sameStream && options.startPositionMs !== undefined) {
            if (playerStateRef.current.paused) {
                playerInstanceRef.current?.play();
            }
        }
        previousProgramInfoRef.current = { ...programInfoRef.current };
        programInfoRef.current.programStartEpochMs = options?.programStartEpochMs ?? 0;
        programInfoRef.current.programEndEpochMs = options?.programEndEpochMs ?? 0;
        programInfoRef.current.preMs = 0;
        programInfoRef.current.postMs = 0;
        programInfoRef.current.streamType = options?.streamType;
        ignoreTimingUpdates.current = true;
        if (!continuousStream && !sameStream) {
            ignoreEventsWhileLoading.current = true;
            psid.current = undefined;
            debounce(callPlay.bind(null, options), PLAY_DEBOUNCE_MS)();
            requestedOptions.current = options;
        }
    }, [
        updateUTVPlayerState,
        callPlay,
        isSameStream,
        updateLastPlayedPosition,
        setLiveProgramInfoCallback,
        clearPositionSyncInterval,
    ]);
    const hasNextItem = useCallback(() => {
        return playOptionQueue.current.length > 0;
    }, []);
    const playNext = useCallback(() => {
        if (hasNextItem()) {
            const nextOptions = playOptionQueue.current.shift();
            if (playerStateRef.current.stream?.streamType === UTVPlayerStreamType.LIVE
                || playerStateRef.current.stream?.streamType === UTVPlayerStreamType.TIMESHIFT) {
                // Avoid requesting new stream for LIVE and TIMESHIFT
                play(nextOptions, true, true);
                return;
            }
            if (playerStateRef.current.stream?.streamType === UTVPlayerStreamType.REPLAY) {
                if (nextOptions?.gt12ChannelInfo) {
                    nextOptions.gt12ChannelInfo = {
                        ...nextOptions.gt12ChannelInfo,
                        // Disable start ad for continuous replay stream
                        disableStartAd: true,
                    };
                }
                play(nextOptions, true);
                return;
            }
            play(nextOptions, true);
        }
    }, [play, hasNextItem]);
    const addPlayerListeners = useCallback(() => {
        const updateCurrentPosition = (event) => {
            let currentMs = event.position * 1000;
            const isDynamicLengthStream = playerInstanceRef.current?.assetType === AssetType.LIVE ||
                playerInstanceRef.current?.assetType === AssetType.REGISTERED_TIMESHIFT;
            if (isDynamicLengthStream) {
                currentMs -= programInfoRef.current.programStartEpochMs;
            }
            else {
                currentMs += playerStateRef.current.streamStartMs ?? 0;
            }
            const end = playerStateRef.current.programEndMs;
            if (end !== undefined
                && !emittedCloseToEnd.current
                && !ignoreEventsWhileLoading.current
                && pendingPlayCall.current === undefined
                && currentMs >= end - PREPARE_NEXT_PROGRAM_MS
                && currentMs < end) {
                updateUTVPlayerState(UTVPlayerEvents.CLOSE_TO_END);
                emittedCloseToEnd.current = true;
            }
            // We need this timings check to playNext for streams that are continuous and does not end
            // e.g. live/timeshift
            if (isDynamicLengthStream
                && end !== undefined
                && playerStateRef.current.playing
                && pendingPlayCall.current === undefined
                && currentMs >= end - PLAY_NEXT_PROGRAM_MS) {
                playNext();
            }
            updateUTVPlayerState(eventToPlayerEvent(event), { currentMs });
        };
        const updatePlaybackState = (event) => {
            switch (event.state) {
                case PlaybackState.STOPPED:
                    clearPositionSyncInterval();
                    if (!ignoreEventsWhileLoading.current) {
                        updateUTVPlayerState(UTVPlayerEvents.STOPPED, {
                            buffering: false,
                            canPlay: false,
                            canPause: false,
                            paused: false,
                            playing: false,
                            stopped: true,
                        });
                    }
                    break;
                case PlaybackState.PLAYING: {
                    ignoreEventsWhileLoading.current = false;
                    startPositionSyncInterval();
                    const updateState = {
                        buffering: false,
                        canPlay: false,
                        paused: false,
                        playing: true,
                        stopped: false,
                    };
                    if (resolveNewAssetTypeForPlayRequested.current) {
                        resolveNewAssetTypeForPlayRequested.current(null);
                        Promise.resolve().then(() => {
                            updateUTVPlayerState(UTVPlayerEvents.PLAYING, updateState);
                        });
                    }
                    else {
                        updateUTVPlayerState(UTVPlayerEvents.PLAYING, updateState);
                    }
                    break;
                }
                case PlaybackState.PAUSED:
                    clearPositionSyncInterval();
                    // SDK triggers PAUSED event right after MEDIA_ENDED, but we consider MEDIA_ENDED as stopped state
                    if (ignoreEventsAfterMediaEnded.current) {
                        ignoreEventsAfterMediaEnded.current = false;
                        return;
                    }
                    if (!ignoreEventsWhileLoading.current) {
                        updateUTVPlayerState(UTVPlayerEvents.PAUSED, {
                            buffering: false,
                            canPlay: true,
                            paused: true,
                            playing: false,
                            stopped: false,
                        });
                    }
                    break;
                case PlaybackState.BUFFERING:
                    clearPositionSyncInterval();
                    updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, {
                        buffering: true,
                    });
                    break;
                default:
                    clearPositionSyncInterval();
                    break;
            }
        };
        const calculateTimings = (seekableRange) => {
            const startMs = seekableRange.start * 1000;
            const endMs = seekableRange.end * 1000;
            const isAbsoluteTime = playerInstanceRef.current?.assetType === AssetType.LIVE ||
                playerInstanceRef.current?.assetType === AssetType.REGISTERED_TIMESHIFT;
            const startRelativeTimeMs = isAbsoluteTime
                ? startMs - programInfoRef.current.programStartEpochMs
                : startMs;
            const endRelativeTimeMs = isAbsoluteTime
                ? endMs - programInfoRef.current.programStartEpochMs
                : endMs;
            const streamEndMs = endRelativeTimeMs - programInfoRef.current.preMs;
            return {
                streamStartMs: Math.max(startRelativeTimeMs - programInfoRef.current.preMs, 
                // User doesn't care about the seekable range before pre-padding
                -programInfoRef.current.preMs),
                streamEndMs,
                programStartMs: 0,
                programEndMs: playerInstanceRef.current?.assetType === AssetType.VOD
                    ? streamEndMs
                    : programInfoRef.current.programEndEpochMs - programInfoRef.current.programStartEpochMs,
                cueRanges: imaCuePointsToUTVCueRanges(adCuePointsRef.current, programInfoRef.current.preMs),
            };
        };
        const updatePlayerState = (event) => {
            let newState = {
                canForward: event.canSeekForward,
                canBackward: event.canSeekBackward,
                canPause: event.canPause,
                // @todo: Check if there is edge case
                canPlayFromStart: true,
            };
            if (event.seekableRange && !ignoreTimingUpdates.current) {
                newState = {
                    ...newState,
                    ...calculateTimings(event.seekableRange),
                };
            }
            updateUTVPlayerState(eventToPlayerEvent(event), newState);
        };
        const updateAudioTracks = (event) => {
            updateUTVPlayerState(eventToPlayerEvent(event), {
                audioTracks: event.tracks.map((track) => ({
                    ...track,
                    label: getISOLanguageName(track, languageNamesRef.current, locale),
                })),
            });
        };
        const updateSubtitlesTracks = (event) => {
            const mappedSubtitleTracks = event.tracks.map((track) => ({
                ...track,
                label: getISOLanguageName(track, languageNamesRef.current, locale),
            }));
            mappedSubtitleTracks.push(OFF_SUBTITLE_TRACK);
            updateUTVPlayerState(eventToPlayerEvent(event), {
                subtitleTracks: mappedSubtitleTracks,
                currentSubtitleTrack: OFF_SUBTITLE_TRACK,
            });
        };
        const updateSelectedAudioTrack = (event) => {
            if (!event.targetTrack) {
                updateUTVPlayerState(eventToPlayerEvent(event), {
                    currentAudioTrack: undefined,
                });
                return;
            }
            updateUTVPlayerState(eventToPlayerEvent(event), {
                currentAudioTrack: {
                    ...event.targetTrack,
                    label: getISOLanguageName(event.targetTrack, languageNamesRef.current),
                },
            });
        };
        const updateSelectedSubtitleTrack = (event) => {
            if (!event.targetTrack) {
                updateUTVPlayerState(eventToPlayerEvent(event), {
                    currentSubtitleTrack: OFF_SUBTITLE_TRACK,
                });
                return;
            }
            updateUTVPlayerState(eventToPlayerEvent(event), {
                currentSubtitleTrack: {
                    ...event.targetTrack,
                    label: getISOLanguageName(event.targetTrack, languageNamesRef.current),
                },
            });
        };
        const updateMedia = (event) => {
            if (playerInstanceRef.current &&
                resolveNewAssetTypeForPlayRequested.current) {
                resolveNewAssetTypeForPlayRequested.current(playerInstanceRef.current.assetType);
            }
            const { assetType } = event.media;
            programInfoRef.current.preMs = event.media.prePadding * 1000;
            programInfoRef.current.postMs = event.media.postPadding * 1000;
            const newState = {
                canPlayLive: assetType === AssetType.REPLAY || assetType === AssetType.REGISTERED_TIMESHIFT,
                paused: true,
            };
            if (playerStateRef.current.stream) {
                newState.stream = {
                    ...playerStateRef.current.stream,
                    streamType: mapAssetTypeToStreamType(assetType, playerStateRef.current.stream.streamType),
                };
            }
            // Send PLAY_REQUESTED if the LIVE stream is switched to REPLAY on MEDIA_CHANGED
            if (playerStateRef.current.stream?.streamType === UTVPlayerStreamType.LIVE &&
                (assetType === AssetType.REPLAY || assetType === AssetType.REGISTERED_TIMESHIFT)) {
                clearPositionSyncInterval();
                updateUTVPlayerState(UTVPlayerEvents.STREAM_READY, newState);
                updateUTVPlayerState(UTVPlayerEvents.AD_CUE_POINTS_AVAILABLE);
                return;
            }
            // Magic to queue this event behind PLAY_REQUESTED
            Promise.resolve().then(() => {
                updateUTVPlayerState(eventToPlayerEvent(event), newState);
            });
        };
        const watchReceivedListener = (event) => {
            unregisteredTimeshiftAvailability.current = event.data.unregistered_timeshift;
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, {
                // @todo: Fix in sdk level so we dont listen to the watch response in this component
                // https://zattoo2.atlassian.net/browse/PLY-1814
                // Note: we use !== false as the value is undefined for streams that can be seeked
                hasSeekBackwardPermission: event.data.stream.replay_seeking_allowed !== false,
                hasSeekForwardPermission: event.data.stream.forward_seeking !== false,
                registeredTimeshiftAvailability: event.data.register_timeshift,
                unregisteredTimeshiftAvailability: event.data.unregistered_timeshift,
                castUrl: event.data.stream.cast_url,
                castLicenseUrl: event.data.stream.cast_license_url,
                adInfoList: event.data.stream.ads,
                prePaddingMs: (event.data.stream.padding?.pre || 0) * 1000,
                postPaddingMs: (event.data.stream.padding?.post || 0) * 1000,
                // @ts-expect-error: @zattoo/zapi doesnt have this type
                timeshiftAllowedOffsetMs: (event.data.registered_at || 0) * 1000,
                // @ts-expect-error: @zattoo/zapi doesnt have this type
                timeshiftRegisterdAt: event.data.registered_at,
            });
            if (event.data.drm_limit_applied || event.data.quality_limited) {
                updateUTVPlayerState(UTVPlayerEvents.QUALITY_RESTRICTED, {}, {
                    quality: event.data.stream.quality,
                    reason: event.data.quality_limited
                        ? QualityRestrictedType.QUALITY_LIMITED
                        : QualityRestrictedType.DRM_LIMITED,
                });
            }
        };
        const updateError = (event) => {
            const error = event.error;
            unregisteredTimeshiftAvailability.current = undefined;
            if (isPinError(error)) {
                triggerError(UTVPlayerErrorType.PIN_ERROR, {
                    stopped: true,
                    playing: false,
                    paused: false,
                }, {
                    code: error?.code,
                    data: error,
                });
                return;
            }
            if (error.code === ErrorCode.ForbiddenAction) {
                triggerError(UTVPlayerErrorType.FORBIDDEN_ACTION, {}, {
                    error,
                });
                return;
            }
            if (error.code === ErrorCode.Source || isZapiPlaybackError(error)) {
                triggerError(UTVPlayerErrorType.STREAM_CANNOT_PLAY, {
                    stopped: true,
                    playing: false,
                    paused: false,
                }, {
                    error,
                });
                return;
            }
            triggerError(UTVPlayerErrorType.UNKNOWN, {}, {
                error,
            });
        };
        const filterOldEvents = (listener) => {
            const newListener = (event) => {
                if (event.psid !== psid.current) {
                    return;
                }
                listener(event);
            };
            return newListener;
        };
        const updateWatchRequested = (event) => {
            psid.current = event.psid;
        };
        const setAdEndedState = () => {
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, {
                ad: undefined,
            });
        };
        const throttleLastUpdatedPosition = throttle(updateLastPlayedPosition, ZAPI_LAST_POSITION_UPDATE_THROTTLE_MS);
        playerInstanceRef.current?.on(EventKey.WATCH_REQUESTED, updateWatchRequested);
        playerInstanceRef.current?.on(EventKey.MEDIA_ENDED, () => {
            updateLastPlayedPosition(previousProgramInfoRef.current?.programStartEpochMs, previousStream.current);
            if (hasNextItem()) {
                playNext();
            }
            else {
                ignoreEventsAfterMediaEnded.current = true;
                updateUTVPlayerState(UTVPlayerEvents.STOPPED, {
                    buffering: false,
                    canPlay: false,
                    canPause: false,
                    paused: false,
                    playing: false,
                    stopped: true,
                });
            }
        });
        playerInstanceRef.current?.on(EventKey.PLAYBACK_STATE_CHANGED, (event) => {
            if (event.state === PlaybackState.PAUSED) {
                throttleLastUpdatedPosition(programInfoRef.current.programStartEpochMs, playerStateRef.current.stream);
            }
            filterOldEvents(updatePlaybackState)(event);
        });
        const listenCurrentStreamEvent = (eventKey, listener) => {
            playerInstanceRef.current?.on(eventKey, filterOldEvents(listener));
        };
        const checkCloseToEnd = (event) => {
            let currentMs = event.position * 1000;
            const isDynamicLengthStream = playerInstanceRef.current?.assetType === AssetType.LIVE ||
                playerInstanceRef.current?.assetType === AssetType.REGISTERED_TIMESHIFT;
            if (isDynamicLengthStream) {
                currentMs -= programInfoRef.current.programStartEpochMs;
            }
            else {
                currentMs += playerStateRef.current.streamStartMs ?? 0;
            }
            const end = playerStateRef.current.programEndMs;
            if (end !== undefined
                && !emittedCloseToEnd.current
                && !ignoreEventsWhileLoading.current
                && pendingPlayCall.current === undefined
                && currentMs >= end - PREPARE_NEXT_PROGRAM_MS) {
                updateUTVPlayerState(UTVPlayerEvents.CLOSE_TO_END);
                emittedCloseToEnd.current = true;
            }
            // We need this timings check to playNext for streams that are continuous and does not end
            // e.g. live/timeshift
            if (isDynamicLengthStream
                && end !== undefined
                && playerStateRef.current.playing
                && pendingPlayCall.current === undefined
                && currentMs >= end - PLAY_NEXT_PROGRAM_MS) {
                playNext();
            }
        };
        listenCurrentStreamEvent(EventKey.SEEKED, checkCloseToEnd);
        listenCurrentStreamEvent(EventKey.CURRENT_POSITION_CHANGED, updateCurrentPosition);
        listenCurrentStreamEvent(EventKey.PLAYER_STATE_CHANGED, updatePlayerState);
        listenCurrentStreamEvent(EventKey.AVAILABLE_AUDIO_TRACKS_CHANGED, updateAudioTracks);
        listenCurrentStreamEvent(EventKey.AVAILABLE_SUBTITLES_TRACKS_CHANGED, updateSubtitlesTracks);
        listenCurrentStreamEvent(EventKey.SELECTED_AUDIO_TRACK_CHANGED, updateSelectedAudioTrack);
        listenCurrentStreamEvent(EventKey.SELECTED_SUBTITLES_TRACK_CHANGED, updateSelectedSubtitleTrack);
        listenCurrentStreamEvent(EventKey.MEDIA_CHANGED, updateMedia);
        listenCurrentStreamEvent(EventKey.PLAYER_ERROR, updateError);
        listenCurrentStreamEvent(EventKey.WATCH_RECEIVED, watchReceivedListener);
        listenCurrentStreamEvent(EventKey.PLAYER_TIMING_READY, (event) => {
            let currentMs = event.data.position * 1000;
            const isDynamicLengthStream = playerInstanceRef.current?.assetType === AssetType.LIVE ||
                playerInstanceRef.current?.assetType === AssetType.REGISTERED_TIMESHIFT;
            if (isDynamicLengthStream) {
                currentMs -= programInfoRef.current.programStartEpochMs;
            }
            else {
                currentMs += playerStateRef.current.streamStartMs ?? 0;
            }
            clearPositionSyncInterval();
            updateUTVPlayerState(UTVPlayerEvents.STREAM_READY, {
                ...calculateTimings(event.data.seekableRange),
                currentMs,
            });
            updateUTVPlayerState(UTVPlayerEvents.AD_CUE_POINTS_AVAILABLE);
            ignoreTimingUpdates.current = false;
        });
        listenCurrentStreamEvent(EventKey.CAST_TRACKS_CHANGED, (event) => {
            updateUTVPlayerState(UTVPlayerEvents.CAST_MEDIA_TRACKS_CHANGED, {}, event.data);
        });
        listenCurrentStreamEvent(EventKey.CAST_TRACK_SELECTION_CHANGED, (event) => {
            updateUTVPlayerState(UTVPlayerEvents.CAST_MEDIA_TRACK_SELECTION_CHANGED, {}, event.data);
        });
        listenCurrentStreamEvent(AdEventKey.AD_METADATA, (event) => {
            /**
             * @todo Checking for adCuePointsRef.current.length === 0 is a hack
             *  but AD_METADATA is triggered twice
             */
            if (adCuePointsRef.current.length === 0) {
                adCuePointsRef.current = event.data.cuePoints;
                updateUTVPlayerState(UTVPlayerEvents.AD_CUE_POINTS_AVAILABLE, {
                    cueRanges: imaCuePointsToUTVCueRanges(adCuePointsRef.current, programInfoRef.current.preMs),
                });
            }
        });
        listenCurrentStreamEvent(AdEventKey.AD_LOADED, (event) => {
            currentAdRef.current = {
                duration: event.data.adInfo.duration,
                // Position is 1-based in the SDK, but tracking needs 0-based
                position: event.data.adInfo.adPodInfo.adPosition - 1,
            };
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, {
                ad: {
                    adId: event.data.adInfo.adId,
                    durationMs: event.data.adInfo.duration * 1000,
                    skipTimeOffsetMs: event.data.adInfo.skipTimeOffset * 1000,
                },
            });
            if (isGt12AdType(event.data.adType)) {
                gt12Tracking.track({
                    type: GT12EventType.AD_RESPONSE,
                    ad_id: adBreakIdRef.current,
                    ad_type: sdkAdTypeToGT12TrackingAdType(event.data.adType),
                    ad_response_length: event.data.adInfo.adPodInfo.maxDuration,
                    ad_count: event.data.adInfo.adPodInfo.totalAds,
                });
            }
        });
        listenCurrentStreamEvent(AdEventKey.AD_STARTED, (event) => {
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, {
                isAdPlayback: true,
                playing: true,
                paused: false,
                stopped: false,
                buffering: false,
                canPlay: false,
                canPause: false,
                ad: {
                    ...playerStateRef.current.ad,
                    adStartMs: playerStateRef.current.currentMs,
                },
            });
            if (isGt12AdType(event.data.adType) && currentAdRef.current) {
                gt12Tracking.track({
                    type: GT12EventType.AD_START,
                    ad_id: adBreakIdRef.current,
                    ad_type: sdkAdTypeToGT12TrackingAdType(event.data.adType),
                    ad_index: currentAdRef.current.position,
                });
            }
        });
        listenCurrentStreamEvent(AdEventKey.AD_ENDED, (event) => {
            if (isGt12AdType(event.data.adType) && currentAdRef.current) {
                gt12Tracking.track({
                    type: GT12EventType.AD_COMPLETE,
                    ad_id: adBreakIdRef.current,
                    ad_type: sdkAdTypeToGT12TrackingAdType(event.data.adType),
                    ad_index: currentAdRef.current.position,
                    asset_length: currentAdRef.current?.duration,
                });
                currentAdRef.current = null;
            }
            setAdEndedState();
        });
        listenCurrentStreamEvent(AdEventKey.AD_SKIPPED, setAdEndedState);
        listenCurrentStreamEvent(AdEventKey.AD_ERROR, (event) => {
            if (isGt12AdType(event.data.adType)) {
                gt12Tracking.track({
                    type: GT12EventType.AD_RESPONSE,
                    ad_id: adBreakIdRef.current,
                    ad_type: sdkAdTypeToGT12TrackingAdType(event.data.adType),
                    response_error: String(event.data.error.code),
                    ad_count: null,
                    ad_response_length: null,
                });
            }
            currentAdRef.current = null;
            setAdEndedState();
        });
        listenCurrentStreamEvent(AdEventKey.AD_BREAK_STARTED, (event) => {
            adBreakIdRef.current = generateAdId();
            updateUTVPlayerState(UTVPlayerEvents.AD_BREAK_STARTED, {
                isChannelAd: event.data.isChannelAd,
            });
            if (isGt12AdType(event.data.adType)) {
                gt12Tracking.track({
                    type: event.data.adType === AdType.START ? GT12EventType.REPLAY : GT12EventType.FAST_FORWARD,
                    stream_type: playerStateRef.current.stream?.streamType === UTVPlayerStreamType.REPLAY
                        ? GT12StreamType.REPLAY
                        : GT12StreamType.RECORDING,
                });
                gt12Tracking.track({
                    type: GT12EventType.AD_REQUEST,
                    ad_type: sdkAdTypeToGT12TrackingAdType(event.data.adType),
                    ad_id: adBreakIdRef.current,
                    ad_request_length: event.data.maxDuration,
                });
            }
        });
        listenCurrentStreamEvent(AdEventKey.AD_BREAK_ENDED, () => {
            adBreakIdRef.current = null;
            updateUTVPlayerState(UTVPlayerEvents.AD_BREAK_ENDED, {
                isChannelAd: false,
                isAdPlayback: false,
                ad: undefined,
            });
        });
        listenCurrentStreamEvent(AdEventKey.PAUSE_AD_REQUESTED, () => {
            adBreakIdRef.current = generateAdId();
            gt12Tracking.track({
                type: GT12EventType.AD_REQUEST,
                ad_id: adBreakIdRef.current,
                ad_type: GT12AdType.PAUSE,
            });
        });
        listenCurrentStreamEvent(AdEventKey.PAUSE_AD_STREAM_PAUSED, () => {
            gt12Tracking.track({
                type: GT12EventType.PAUSE,
            });
        });
        listenCurrentStreamEvent(AdEventKey.PAUSE_AD_PARSED, (event) => {
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, {
                pauseAd: {
                    url: event.data.url,
                    height: event.data.height,
                    width: event.data.width,
                },
            });
            gt12Tracking.track({
                type: GT12EventType.AD_RESPONSE,
                ad_id: adBreakIdRef.current,
                ad_type: GT12AdType.PAUSE,
                ad_count: null,
                ad_response_length: null,
            });
        });
        listenCurrentStreamEvent(AdEventKey.AD_BREAK_SKIP_AVAILABLE, () => {
            updateUTVPlayerState(UTVPlayerEvents.AD_BREAK_SKIP_AVAILABLE, {
                skipAdAvailable: true,
            });
        });
        listenCurrentStreamEvent(AdEventKey.AD_BREAK_SKIP_UNAVAILABLE, () => {
            updateUTVPlayerState(UTVPlayerEvents.STATE_UPDATED, {
                skipAdAvailable: false,
            });
        });
    }, [
        locale,
        updateUTVPlayerState,
        triggerError,
        updateLastPlayedPosition,
        playNext,
        hasNextItem,
        startPositionSyncInterval,
        clearPositionSyncInterval,
    ]);
    useEffect(() => {
        const initializePlayer = async () => {
            // Immediately set to null to prevent further usage
            const playerToDestroy = playerInstanceRef.current;
            playerInstanceRef.current = null;
            setPlayerCreated(false);
            playerToDestroy?.destroy();
            await globalInit();
            programInfoRef.current = initialProgramInfo;
            let logging;
            if (debug) {
                const consoleLogger = new ConsoleLogger();
                const playerLogger = new ChannelLogger('PlaybackSDK::Player', consoleLogger);
                const adapterLogger = new ChannelLogger('PlaybackSDK::Adapter', consoleLogger);
                logging = {
                    playerLogger,
                    adapterLogger,
                };
            }
            const results = await Promise.allSettled([
                createPlayer({
                    enableEventDispatcher,
                    enableMediaSession,
                    host,
                    publicId,
                    appVersion,
                    appId,
                    appName: '',
                    locale,
                    stepBackwardDuration,
                    stepForwardDuration,
                    license,
                    logging: !debug ? undefined : logging,
                    latencyMeasurementInterval: 60,
                }),
                getLanguageNames(),
            ]);
            // Handle player creation result
            if (results[0].status === 'fulfilled') {
                // If this goes here we have a problem with the player instance during recreation
                if (playerInstanceRef.current) {
                    debugLogger(new Error('Player is unexpectedly already created'));
                    return;
                }
                playerInstanceRef.current = results[0].value;
                addPlayerListeners();
                gt12Tracking.init({
                    appId,
                    isProd: isAnalyticsProd,
                });
                setPlayerCreated(true);
                updateUTVPlayerState(UTVPlayerEvents.INITIALIZED, {
                    volume: playerInstanceRef.current.volume,
                });
            }
            else {
                debugLogger(results[0].reason);
                return;
            }
            // Handle language names result
            if (results[1].status === 'fulfilled') {
                languageNamesRef.current = results[1].value;
            }
            else {
                triggerError(UTVPlayerErrorType.UNKNOWN, {}, results[1].reason);
            }
        };
        initializePlayer();
        return () => {
            updateLastPlayedPosition(programInfoRef.current.programStartEpochMs, playerStateRef.current.stream);
            clearPositionSyncInterval();
            // Immediately set to null to prevent further usage
            const playerToDestroy = playerInstanceRef.current;
            playerInstanceRef.current = null;
            // Destroy asynchronously but don't wait for it
            playerToDestroy?.destroy().catch(debugLogger);
            gt12Tracking.destroy();
        };
    }, [
        appId, appVersion, host, onEvent, publicId, stepBackwardDuration,
        stepForwardDuration, updateUTVPlayerState, triggerError, locale,
        playNext, debug, license, updateLastPlayedPosition,
        addPlayerListeners, debugLogger, isAnalyticsProd,
        enableEventDispatcher, enableMediaSession, isCastEnabled,
        clearPositionSyncInterval,
    ]);
    const seekToAdBreakEnd = useCallback(() => {
        playerInstanceRef.current?.seekToAdBreakEnd();
    }, []);
    const setAudioTrack = useCallback((id) => {
        setMediaTrackById(id, playerStateRef.current.audioTracks, playerInstanceRef.current?.setAudioTrack.bind(playerInstanceRef.current));
    }, []);
    const setSubtitleTrack = useCallback((id) => {
        if (id === 'off') {
            playerInstanceRef.current?.setSubtitlesTrack(null);
            return;
        }
        setMediaTrackById(id, playerStateRef.current.subtitleTracks, playerInstanceRef.current?.setSubtitlesTrack.bind(playerInstanceRef.current));
    }, []);
    const cuePlay = useCallback((options) => {
        playOptionQueue.current = [options];
    }, []);
    const playPause = useCallback(() => {
        // @todo Check what do we do when the player is buffering
        if (playerInstanceRef.current?.currentState === PlaybackState.PLAYING) {
            playerInstanceRef.current?.pause();
        }
        else if (playerInstanceRef.current?.currentState === PlaybackState.PAUSED) {
            play();
        }
    }, [play]);
    const pause = useCallback(() => {
        playerInstanceRef.current?.pause();
    }, []);
    const stop = useCallback(() => {
        // save the position on stream stop
        updateLastPlayedPosition(programInfoRef.current?.programStartEpochMs, playerStateRef.current?.stream);
        playerInstanceRef.current?.stop();
    }, [updateLastPlayedPosition]);
    const setStatsForNerds = useCallback((isEnabled) => {
        playerInstanceRef.current?.setStatsForNerds(isEnabled);
    }, []);
    const requestAdSkip = useCallback(() => {
        playerInstanceRef.current?.requestAdSkip();
    }, []);
    const trackPauseAdEvent = useCallback((eventType, sentForAdIdRef) => {
        if (!playerStateRef.current.pauseAd ||
            !adBreakIdRef.current ||
            sentForAdIdRef.current === adBreakIdRef.current) {
            return;
        }
        sentForAdIdRef.current = adBreakIdRef.current;
        gt12Tracking.track({
            type: eventType,
            ad_id: adBreakIdRef.current,
        });
    }, []);
    const trackPauseAdDisplayed = useCallback(() => {
        trackPauseAdEvent(GT12EventType.AD_PAUSE_DISPLAY, pauseAdDisplayedSentForAdIdRef);
    }, [trackPauseAdEvent]);
    const trackPauseAdTenSecondsDisplayed = useCallback(() => {
        trackPauseAdEvent(GT12EventType.AD_PAUSE_10_SEC, pauseAdTenSecondsDisplayedSentForAdIdRef);
    }, [trackPauseAdEvent]);
    useImperativeHandle(ref, () => ({
        stop,
        play,
        pause,
        playPause,
        seek,
        seekTo,
        seekToAdBreakEnd,
        volumeUp,
        volumeDown,
        setVolume,
        setProgramInfo,
        toggleMute,
        setAudioTrack,
        setSubtitleTrack,
        cuePlay,
        setStatsForNerds,
        requestAdSkip,
        trackPauseAdTenSecondsDisplayed,
        trackPauseAdDisplayed,
        saveStreamProgressPosition,
    }), [
        stop,
        play,
        seek,
        seekTo,
        seekToAdBreakEnd,
        setAudioTrack,
        setSubtitleTrack,
        pause,
        setProgramInfo,
        playPause,
        cuePlay,
        volumeUp,
        volumeDown,
        setVolume,
        toggleMute,
        setStatsForNerds,
        requestAdSkip,
        trackPauseAdDisplayed,
        trackPauseAdTenSecondsDisplayed,
        saveStreamProgressPosition,
    ]);
    if (!playerCreated || !playerInstanceRef.current) {
        return null;
    }
    const isCustomIma = playerInstanceRef.current.isCustomIma;
    return (React.createElement(React.Fragment, null,
        isCustomIma && React.createElement(SkipAdButton, { player: playerInstanceRef.current }),
        React.createElement(PlayerView, { player: playerInstanceRef.current, style: style })));
});
